import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, History, Info, ShieldCheck, Dices, X, UserRound } from 'lucide-react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import {
  createTeimosinha, fetchMilharStatus, placeMilharBet, type MilharBet,
} from '../../features/milhar/api';
import { CoPickerHint } from '../../features/milhar/CoPickerHint';
import { MaduroBanner } from '../../features/milhar/MaduroBanner';
import { MilharHowSheet } from '../../features/milhar/MilharHowSheet';
import { MilharVerifyOverlay } from '../../features/milhar/MilharVerifyOverlay';
import { RoundPicksSheet } from '../../features/milhar/RoundPicksSheet';
import { RoundFeed } from '../../features/milhar/RoundFeed';
import { RoundBuyersSheet } from '../../features/milhar/RoundBuyersSheet';
import { RoundDetailSheet } from '../../features/milhar/RoundDetailSheet';
import { BetConfirmSheet } from '../../features/milhar/BetConfirmSheet';
import { PublicProfileSheet, profileSeed } from '../../features/profile/PublicProfileSheet';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCompactParts, fmtCurrency, fmtMoney } from '../../shared/lib/format';
import { clockSkewSec } from '../../shared/lib/clockSkew';
import { MILHAR_TIERS, type MilharTierId } from '../../shared/milhar/tiers';
import { countdownView } from '../../shared/milhar/countdownView';
import {
  GATE_BLOCKS, RESULT_TOTAL_HOLD_SEC, captureDecision, captureHeldDraw, cellsArmed,
  drawGateView, gateCells, gateCellsComplete, gatePollMs, gateExpired, heldResultView,
  justDrawn,
} from '../../shared/milhar/drawGate';
import {
  bestTicket, fireworkCount, fireworkParticles, hitTier, rankedTickets, stageRoundIdLabel,
} from '../../shared/milhar/drawStage';
import { ctaState, summaryParts, totalCents } from '../../shared/milhar/orderMath';
import { poolRoundsLine } from '../../shared/milhar/poolView';
import { hasFeed, injectionFromAmount } from '../../shared/milhar/roundFeedView';
import {
  pickerPayoutSlots, poolRoundsParts, roundsStepper, stepRounds,
} from '../../shared/milhar/poolHeadView';
import { betConfirmText, milharBetErrorKey } from '../../shared/milhar/betFeedback';
import {
  digitStateAt, nextRevealRound, REVEAL_WINDOW_MS,
} from '../../shared/milhar/revealTimeline';
import {
  clearPick, emptyPick, focusAt, isComplete, pickString, randomPick, setDigit,
} from '../../shared/milhar/pickState';
import { ticketCells } from '../../shared/milhar/ticketView';
import { localListTime } from '../../shared/lib/datetime';
import { headlineBet, showsCoinRain, winTier } from '../../shared/milhar/winFeedback';
import { winnerAvatarUrl, winnerRoundText } from '../../shared/milhar/winnersView';

type MilharReveal = {
  round: number;
  roundLabel: string;
  result: string;
  bets: MilharBet[];
};

/**
 * Milhar 主页。视觉规格 `doc/design/MILHAR_VISUAL_SPEC.md` §5–§13。
 *
 * <p>全部逻辑都在 `shared/milhar` 的纯函数里（本仓库前端只能测纯 TS，没有
 * jsdom/RTL），本组件只负责渲染与事件接线。
 *
 * <p>两条不能违反的：**展示的金额是 takeHome（池子 × 80%）不是 poolTotal**；
 * **WAITING 相位禁止显示任何倒计时数字**。
 */
export function MilharPage({ tier = '4' }: { tier?: MilharTierId }) {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const setRoute = useAppStore((state) => state.setRoute);
  const setBetHistoryReturn = useAppStore((state) => state.setBetHistoryReturn);
  const showToast = useAppStore((state) => state.showToast);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const countryCode = useCountryStore((state) => state.country.code);
  const queryClient = useQueryClient();

  const tierSpec = MILHAR_TIERS.find((entry) => entry.id === tier) ?? MILHAR_TIERS[0];
  const ledgerRoute = tier === '5' ? 'milhar.hora.ledger' : 'milhar.ledger';
  const milharRoute = tier === '5' ? 'milhar.hora' : 'milhar';
  const statsRoute = tier === '5' ? 'milhar.hora.stats' : 'milhar.stats';
  const status = useQuery({
    queryKey: ['milhar-status', tier, token || 'guest', countryCode],
    queryFn: () => fetchMilharStatus(token, tier),
    // Computing 期间提速到 1 秒 —— 那几秒的等待大部分是轮询间隔，不是开奖本身。
    // 口径与理由都在 gatePollMs 里，别在这儿写死一个数字。
    refetchInterval: (query) => {
      const now = Math.floor(Date.now() / 1000);
      const drawingNow = query.state.data?.drawing;
      return gatePollMs(
        gateExpired(drawingNow, now) ? null : drawGateView(drawingNow, now),
      );
    },
  });

  const digits = status.data?.config.digits ?? tierSpec.digits;
  const [pick, setPick] = useState(() => emptyPick(tierSpec.digits));
  const [rounds, setRounds] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [picksOpen, setPicksOpen] = useState(false);
  const [buyersOpen, setBuyersOpen] = useState(false);
  // 点开某一期的详情。和奖池账单里点期号进的是**同一个 sheet** —— 玩家问的是
  // 同一个问题（这期发生了什么），不该有两套答案。
  const [detailRound, setDetailRound] = useState<number | null>(null);
  const [howOpen, setHowOpen] = useState(false);
  const [verifyRound, setVerifyRound] = useState<{ round: number; label: string; result: string } | null>(null);
  // 两档共用这一个组件实例（App 里 tier 只是 prop），从 #/milhar-hora 跳到 #/milhar
  // 不会重挂载。弹层和核对层都带着「哪一期」，让它们跨档位活下来就是拿 5 位档的
  // 期去套 4 位档的窗口 —— 核对页会当场报「对不上」。
  useEffect(() => { setHowOpen(false); setVerifyRound(null); }, [tier]);
  // 就地开公开资料。定稿 §3.2 —— **不要改成跳排行榜页再跳回**：pick / 期数 /
  // 数量都是这个组件的本地 state，跳页一次全没。
  // avatarUrl 是可选的：买家名单那条路（RoundBuyersSheet.onOpenProfile）只给
  // userId 与 username，头奖名单这条路能多给一个头像，让弹层一打开就是对的。
  const [profileFor, setProfileFor] = useState<
    { userId: number; username: string; avatarUrl?: string } | null
  >(null);
  // 注入微动的一次性标记。定稿 §4.1：它必须与轮播换条同帧发生 ——
  // 因果可见是整块奖池氛围的立论，各走各的节奏就只是三个打架的装饰。
  const [injection, setInjection] = useState<{ amount: string; at: number } | null>(null);
  // 「更多选项」默认折叠。收起时标题行显示当前值摘要，不展开也知道自己选了什么。
  const [optionsOpen, setOptionsOpen] = useState(false);
  // 刚下注成功的那一张票。**记 id 而不是时间戳** —— 按「pending 就高亮」会把
  // 已有的几张待开奖票一起点亮，那不是「新票高亮」，是「所有票闪一下」。
  const [freshBet, setFreshBet] = useState<{ betId?: number; subscriptionId?: number } | null>(null);
  // 下注回执弹窗。评审 2026-08-19：toast 太小，确认要有分量。
  const [confirm, setConfirm] = useState<
    {
      pick: string; rounds: number; quantity: number; totalCents: number;
      /**
       * 这笔票的**首期**，取自下单接口的响应。
       *
       * <p>别用页面上的当前期号顶替：两者曾经不是同一个数，而收据是玩家事后唯一能
       * 回忆「我买的是哪一期」的凭据，写错就是骗人。工程契约 §1.7 一直在返回
       * `firstRound`，前端此前把它丢了。
       */
      firstRound: number;
    } | null>(null);
  const [teimoTipOpen, setTeimoTipOpen] = useState(false);
  const [padOpen, setPadOpen] = useState<number | null>(null);
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<MilharReveal | null>(null);
  const [revealElapsedMs, setRevealElapsedMs] = useState(0);
  const pendingSubmission = useRef<{ signature: string; key: string } | null>(null);
  const revealInitialized = useRef(false);
  const pendingBetIds = useRef<Set<number>>(new Set());
  const revealedBetIds = useRef<Set<number>>(new Set());

  const pickText = pickString(pick);
  // No price until the round says so. The old fallback of '2' was Brazil's
  // R$ 2 ticket, and on a cold start an Argentine saw "AR$ 2" for a beat
  // before the real AR$ 1.500 arrived (2026-09-15). A figure that is not yet
  // known renders as a placeholder, never as a plausible wrong number.
  const priceKnown = Boolean(status.data?.config.ticketPrice);
  const priceCents = priceKnown ? Math.round(Number(status.data!.config.ticketPrice) * 100) : 0;
  const total = totalCents(priceCents, quantity, rounds);
  const phase = status.data?.round.phase === 'WAITING' ? 'waiting' : 'open';

  useEffect(() => {
    setPick(emptyPick(digits));
    setPadOpen(null);
    setError(null);
  }, [digits, tier]);

  // 每秒重算一次倒计时；status 本身 5 秒才刷一次，光靠它钟会一跳五格
  // **倒计时走服务端校正过的时钟，不是手机时钟。** 手机差多少倒计时就差多少，
  // 而这个 nowSec 还驱动开奖闸门的安全阀和结果定格 —— 手机快 30 秒的话，闸门
  // 一出现就被判定过期，整段开奖演出不会发生（2026-08-19 真机反馈）。
  const skewSec = useMemo(
    () => clockSkewSec(status.data?.serverTime, status.dataUpdatedAt),
    [status.data?.serverTime, status.dataUpdatedAt]);
  const [deviceSec, setDeviceSec] = useState(() => Math.floor(Date.now() / 1000));
  const nowSec = deviceSec + skewSec;
  useEffect(() => {
    const timer = window.setInterval(() => setDeviceSec(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const clock = countdownView(status.data?.round.endsAt ?? 0, nowSec, phase);
  // 开奖闸门。上一期没开出来之前不接单——规则在后端（PREV_ROUND_DRAWING），
  // 这里只负责把这段等待演出来，并且不去邀请一个必然被拒的动作。
  const drawing = status.data?.drawing;
  const gate = gateExpired(drawing, nowSec) ? null : drawGateView(drawing, nowSec);
  // 闸门放开后把刚开出的号定格几秒。没有这一段，玩家等完三格 + Computing 之后
  // 画面直接跳到下一期倒计时 —— 他等的那个东西从来没在他眼前出现过。
  const [heldDraw, setHeldDraw] = useState<
    { round: number; result: string; atSec: number; atMs: number;
      mine?: boolean; payout?: string | null } | null>(null);
  const gateRoundRef = useRef<{ round: number; sinceMs: number } | null>(null);
  useEffect(() => {
    const active = gate ? drawing?.round ?? null : null;
    if (active) {
      if (gateRoundRef.current?.round !== active) {
        gateRoundRef.current = { round: active, sinceMs: performance.now() };
      }
      return;
    }
    const pending = gateRoundRef.current;
    if (!pending) return;
    // 闸门可能是被后端撤掉 drawing 关的（结果到了），也可能是本地安全阀到点关的
    // （结果还没到）。**两者不能一视同仁** —— 见 captureDecision 的说明，
    // 那正是真机上「Computing 之后什么都没有」的根因。
    const decision = captureDecision(
      pending.round, status.data?.recentResults, performance.now() - pending.sinceMs);
    if (decision === 'giveup') { gateRoundRef.current = null; return; }
    if (decision === 'wait') return; // 保留期号，等下一次轮询
    const drawn = justDrawn(pending.round, status.data?.recentResults);
    if (!drawn) return;
    gateRoundRef.current = null;
    // mine/payout 一并记下 —— 落定之后要据此给中奖/未中的反馈
    const row = status.data?.recentResults?.find((r) => r.round === drawn.round);
    // atSec 用服务端校正过的 nowSec，不是 Date.now() —— 定格是否过期由 heldResultView
    // 用同一个 nowSec 判定，两者不同源时手机慢十几秒就会让开奖演出「一出生就过期」
    // 被整段跳过（见 captureHeldDraw / clockSkew.ts）。atMs 走单调时钟管逐位落定。
    setHeldDraw(captureHeldDraw(
      drawn,
      { sec: nowSec, ms: performance.now() },
      { mine: row?.mine, payout: row?.payout ?? null },
    ));
  }, [gate, drawing?.round, status.data?.recentResults]);
  const held = heldResultView(heldDraw, nowSec, RESULT_TOTAL_HOLD_SEC);
  // 逐位落定要 60fps 的时钟，nowSec 那个每秒一跳的不够用
  const [settleMs, setSettleMs] = useState(0);
  useEffect(() => {
    if (!heldDraw) { setSettleMs(0); return undefined; }
    let raf = 0;
    const tick = () => {
      setSettleMs(performance.now() - heldDraw.atMs);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [heldDraw]);
  // Computing 期间 result 传 null —— 全是 `–`，最后一位在闪
  const cells = gateCells(digits, held ? held.result : null, settleMs);
  // 「参与但没中」不再需要单独的颜色标记 —— 对照行本身就在那里，
  // 号码摆在开奖号正下方、一位都没亮，这比一个绿框说得更清楚。

  // ── 开奖台 ────────────────────────────────────────────────────────────────
  // 闸门期间选号器反正不能用，整块让给开奖。不是找空地：每一格下面本来就写着
  // 这一档值多少钱，而开奖正好从右往左落 —— 数字落进哪一格，它值多少就在正下方。
  const stageOn = !!gate || !!held;
  // 已落定的位数（从右数）。Computing 阶段是 0，全是 `–`。
  const stageLanded = cells.filter((c) => c.state === 'settled').length;
  // 我在**被开的那一期**的注单。myBets 覆盖当期 ± 一档，正好含上一期。
  const stageRound = held?.round ?? status.data?.drawing?.round ?? null;
  // 开奖台上那一期的日内期号，给页头的期号条用 —— 后端一封盘就把 current 轮到了
  // 下一期，页头不能跟着跳到下一期（「194 还没开，上面就显示 195」）。
  const stageIdLabel = stageRoundIdLabel(
    stageRound,
    status.data?.recentResults,
    status.data?.drawing?.endsAt,
    tierSpec.cadenceMinutes * 60,
  );
  const stageTickets = (status.data?.myBets || [])
    .filter((b) => b.round === stageRound)
    .map((b) => ({ id: b.id, pick: b.pick }));
  // 每落定一位重算一次，把此刻最好的那张顶上去（并列取先买的）
  const stageBest = bestTicket(stageTickets, held?.result ?? null, stageLanded);
  // 全部列出来，按此刻命中数排序。只显示一张比不了 —— 见 rankedTickets。
  const stageRanked = rankedTickets(stageTickets, held?.result ?? null, stageLanded);
  const stageTier = hitTier(stageBest?.hits ?? 0, digits);
  // 三格走完之前不闪 —— 闪是「下一个就是它了」的信号，提前放就不新鲜了
  const cellArmed = cellsArmed(gate);
  // 烟花只在**全部落定之后**放。少一位没落定就先放，等于提前剧透结局。
  const stageLanded_ = gateCellsComplete(cells);
  const fireworks = stageLanded_ && stageTier !== 'none'
    ? fireworkParticles(
        fireworkCount(stageTier),
        stageTier === 'jackpot' ? 150 : stageTier === 'high' ? 115 : 85)
    : [];

  useEffect(() => {
    revealInitialized.current = false;
    pendingBetIds.current = new Set();
    revealedBetIds.current = new Set();
    setReveal(null);
  }, [tier, token]);

  // Keep a bet pinned across the round boundary. Only bets actually observed
  // pending in this mounted page can trigger a reveal, so opening the page does
  // not replay old history. The authoritative result/status both come from the
  // backend; the client merely sequences their presentation.
  useEffect(() => {
    const bets = status.data?.myBets;
    if (!bets) return;
    if (!revealInitialized.current) {
      for (const bet of bets) {
        if (bet.status === 'pending' || bet.status === 'locked') pendingBetIds.current.add(bet.id);
        else revealedBetIds.current.add(bet.id);
      }
      revealInitialized.current = true;
      return;
    }
    for (const bet of bets) {
      if (bet.status === 'pending' || bet.status === 'locked') pendingBetIds.current.add(bet.id);
    }
    if (reveal) return;
    const round = nextRevealRound(bets, pendingBetIds.current, revealedBetIds.current);
    if (round !== null) {
      const roundBets = bets.filter((bet) =>
        bet.round === round && (bet.status === 'won' || bet.status === 'lost'));
      const result = roundBets.find((bet) => bet.result)?.result;
      if (roundBets.length && result) {
        for (const bet of roundBets) {
          pendingBetIds.current.delete(bet.id);
          revealedBetIds.current.add(bet.id);
        }
        // **只有头奖才全屏接管。** 普通输赢已经由开奖台逐位演过一遍了，
        // 再弹一张卡是把同一件事演两次，而且它会盖住开奖台本身
        // （2026-08-19 真机反馈）。头奖是这个玩法存在的理由，它值得占满屏幕；
        // 中 R$ 4 不值得。
        const top = headlineBet(
          roundBets.map((bet) => ({ ...bet, payout: Number(bet.payout) })));
        const jackpot = top !== null
          && winTier(top.matched, top.payout, digits) === 'jackpot';
        if (jackpot) {
          setReveal({
            round,
            roundLabel: roundBets[0].roundLabel,
            result,
            bets: roundBets,
          });
        }
      }
    }
    // VOID/refund has no draw to animate, but must leave the pending set and
    // remain visible as a cancelled ticket in the list.
    for (const bet of bets) {
      if (bet.status !== 'pending' && bet.status !== 'locked'
          && bet.status !== 'won' && bet.status !== 'lost') {
        pendingBetIds.current.delete(bet.id);
        revealedBetIds.current.add(bet.id);
      }
    }
  }, [reveal, status.data?.myBets]);

  useEffect(() => {
    if (!reveal) return undefined;
    const startedAt = performance.now();
    setRevealElapsedMs(0);
    const ticker = window.setInterval(
      () => setRevealElapsedMs(performance.now() - startedAt), 80);
    const close = window.setTimeout(() => setReveal(null), REVEAL_WINDOW_MS + 300);
    return () => {
      window.clearInterval(ticker);
      window.clearTimeout(close);
    };
  }, [reveal]);

  // DOM 焦点跟着逻辑焦点走。**只能用 effect，不能在 typeDigit 里直接搬** ——
  // 键盘输入推进的是 pick.focus，若同时用 rAF 搬 DOM 焦点，连打时下一次按键
  // 会落在还没搬完的那一格上（就是 fca44d5f 修的那个 bug）。
  // 不跟的话则是另一个毛病：点过的那格留着浏览器焦点环，屏幕上同时亮两格。
  useEffect(() => {
    if (padOpen === null) return;
    cellRefs.current[pick.focus]?.focus();
  }, [pick.focus, padOpen]);

  // 「1 aposta × 1 rodada」—— 量词随数量变形，见 summaryParts
  const parts = summaryParts(quantity, rounds);
  const summaryLine = t('milharSummary')
    .replace('%q%', parts['%q%'])
    .replace('%qu%', t(parts['%qu%']))
    .replace('%r%', parts['%r%'])
    .replace('%ru%', t(parts['%ru%']));
  const pickPrompt = t('milharPickPrompt').replace('%d%', String(digits));

  const cta = ctaState({
    complete: isComplete(pick),
    phase: gate ? 'waiting' : phase,
    balanceCents: 0,          // 余额由后端判定；前端只在收到 INSUFFICIENT_BALANCE 后改文案
    totalCents: 0,
    unavailable: error === 'MILHAR_ROUND_RISK_LIMIT',
    submitting,
    guest: guestMode || !token,
  });

  const ctaLabel = useMemo(() => {
    switch (cta) {
      case 'incomplete': return t('milharCtaIncomplete').replace('%d%', String(digits));
      case 'waiting': return t('milharCtaWaiting');
      case 'unavailable': return t('milharCtaUnavailable');
      case 'guest': return t('milharCtaGuest');
      case 'insufficient': return t('milharCtaDeposit');
      default: return priceKnown ? t('milharCtaReady').replace('%v%', fmtMoney(total / 100)) : t('loading');
    }
  }, [cta, digits, t, total, priceKnown]);

  /**
   * 选中一个数字：填入、跳到下一位、面板和 DOM 焦点都跟着走。
   *
   * 用函数式更新而不是 `setDigit(pick, …)` —— 后者读的是渲染闭包里的 `pick`，
   * 打字快一点连续按四下，后三次都基于同一份过期状态，只有最后一位落得下。
   */
  function chooseDigit(index: number, value: number) {
    setPick((prev) => setDigit(prev, index, value));
    setPick((prev) => focusAt(prev, Math.min(index + 1, digits - 1)));
    setPadOpen(index < digits - 1 ? index + 1 : null);
    // DOM 焦点由上面那个 effect 跟着 pick.focus 搬，这里不再自己动手
  }

  /**
   * 键盘输入一个数字。**按逻辑焦点走，不按 DOM 焦点走** ——
   * DOM 焦点是 requestAnimationFrame 异步移动的，打字快时下一次按键
   * 可能还落在原来那一格。逻辑焦点在同一次状态更新里推进，没有这个时序问题。
   *
   * 打字总是往前走，即使这一格原本已有数字 —— 重打一遍时不该原地踏步。
   */
  function typeDigit(value: number) {
    setPick((prev) => {
      const at = prev.focus;
      const filled = setDigit(prev, at, value);
      return at < digits - 1 ? focusAt(filled, at + 1) : filled;
    });
    // 打满最后一位就收起面板 —— 和 chooseDigit 一致。
    // 面板占了半屏，不收起来用户看不到自己刚打出来的号，也够不到下单按钮。
    setPadOpen((prev) => (prev === null || prev >= digits - 1 ? null : prev + 1));
  }

  /** 键盘输入 —— 桌面和无障碍都需要，光靠点按面板不够。 */
  function onDigitKey(e: ReactKeyboardEvent, index: number) {
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      typeDigit(Number(e.key));
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      setPick(clearPick(digits));
      setPadOpen(0);
      requestAnimationFrame(() => cellRefs.current[0]?.focus());
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      setPick((prev) => focusAt(prev, index - 1));
      setPadOpen(padOpen === null ? null : index - 1);
    }
    if (e.key === 'ArrowRight' && index < digits - 1) {
      e.preventDefault();
      setPick((prev) => focusAt(prev, index + 1));
      setPadOpen(padOpen === null ? null : index + 1);
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      setPick((prev) =>
        setDigit(prev, index, ((prev.digits[index] ?? 0) + (e.key === 'ArrowUp' ? 1 : 9)) % 10));
    }
  }

  async function submit() {
    if (!token) { setRoute('auth.login'); return; }
    if (!pickText) return;
    setSubmitting(true);
    setError(null);
    try {
      const signature = `${tier}:${pickText}:${quantity}:${rounds}:${status.data?.config.ticketPrice ?? '2.00'}`;
      if (pendingSubmission.current?.signature !== signature) {
        pendingSubmission.current = {
          signature,
          key: crypto.randomUUID?.() ?? `milhar-${Date.now()}-${pickText}`,
        };
      }
      const key = pendingSubmission.current.key;
      let placed: { betId?: number; subscriptionId?: number } = {};
      // 首期号以**接口返回的**为准，页面上的当前期只是兜底
      let firstRound = status.data?.round.number ?? 0;
      if (rounds === 1 && quantity === 1) {
        const res = await placeMilharBet(token, {
          pick: pickText,
          amount: status.data?.config.ticketPrice ?? '2.00',
          idempotencyKey: key,
          tier,
        });
        placed = { betId: res.betId };
      } else {
        const res = await createTeimosinha(token, {
          pick: pickText,
          stake: status.data?.config.ticketPrice ?? '2.00',
          quantity, rounds, idempotencyKey: key, tier,
        });
        placed = { subscriptionId: res.subscriptionId };
        if (Number.isFinite(res.firstRound)) firstRound = res.firstRound;
      }
      pendingSubmission.current = null;
      // §10.3：确认必须看得见。在这之前，成功与失败在屏幕上长得一模一样
      // （号被清空，别无提示），玩家会以为没点上然后重复点 —— 重复下注是这个
      // 洞真正的代价。
      setConfirm({ pick: pickText, rounds, quantity, totalCents: total, firstRound });
      setPick(clearPick(digits));
      setFreshBet(placed);
      queryClient.invalidateQueries({ queryKey: ['milhar-status'] });
    } catch (e) {
      const code = e instanceof Error ? e.message : 'UNKNOWN';
      setError(code);
      // 每一个码都要出话。原来只渲染 MILHAR_ROUND_RISK_LIMIT，其余全被吞掉。
      showToast(t(milharBetErrorKey(code)), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  const pool = status.data?.pool;
  const roundsParts = poolRoundsParts(pool?.roundsAccumulating, t('milharPoolRounds'));
  // 赔率来自 config，不写死 —— 调档时写死的表会当场变成假信息（既有硬约束）
  const payoutSlots = pickerPayoutSlots(digits, status.data?.config.multipliers, priceCents);
  const confirmIsCurrentRound = confirm != null
    && status.data != null
    && confirm.firstRound === status.data.round.number;

  // 期数步进：后端给了 maxRounds 才按 +1 走，否则退回档位（见 roundsStepper）
  const roundsStep = roundsStepper(
    status.data?.config.teimosinhaOptions ?? [1, 5, 10, 20],
    status.data?.config.maxRounds,
  );
  // 这个区块叫「本期下注」，就只放本期的。往期属于「全部投注记录」——
  // 后端为了让 teimosinha 的剩余期数算得出来，会多带回一档过往注单，
  // 那是数据需要，不是这个区块该展示的东西（评审 2026-08-19）。
  const currentRoundBets = (status.data?.myBets || [])
    .filter((bet) => bet.round === status.data?.round.number);
  const maxQuantity = status.data?.config.maxQuantity ?? 20;
  // 注入口径的两个比例。**都来自 config，前端不写死** —— 见 injectionFromAmount。
  const contribPct = status.data?.config.jackpotContribPct;
  const retainPct = status.data?.config.seedRetainPct;

  if (status.data && !status.data.available) {
    return (
      <section className="ds-screen milhar-screen">
        <header className="ds-header shrink-0">
          <button className="icon-button tap" type="button"
            onClick={() => setRoute('lobby')} aria-label={t('back')}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">{t(tierSpec.titleKey)}</h1>
          {/* 同一颗常驻入口，理由同下方主返回里的那颗注释：betting 关闭时（运维
              停售）Milhar 页会走这条早退分支渲染，但 /stats 只看 enabled && visible，
              跟 bettingEnabled 无关 —— 结果页照样能打开，只是这颗按钮原本没跟着渲染，
              玩家想看自己这期开没开都摸不到入口（2026-09-11 复盘 Finding 1）。 */}
          <button type="button" className="milhar-head-results tap"
            onClick={() => setRoute(statsRoute)}>
            <History size={15} aria-hidden="true" />
            <span>{t('milharResultsPageTitle')}</span>
          </button>
        </header>
        <div className="milhar-sec">
          <p className="milhar-unavailable">{t('milharCtaUnavailable')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="ds-screen milhar-screen">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button"
          onClick={() => setRoute('lobby')} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">{t(tierSpec.titleKey)}</h1>
        {/* 开奖结果入口。**放在页头而不是第一屏内容里**：页头在滚动容器外面
            （下面那个 overflow-y-auto 才是内容），所以这颗按钮在用户选号、看注单、
            滚到任何位置时都在。放进内容里划两下就没了，只解决一半问题。
            它也刻意不依赖 status.data —— 奖池和期号条要等接口才渲染，这颗按钮
            是骨架的一部分，一进页面就在，不会闪一下才出现。
            页面底部那排开奖号旁边的 Ver tudo 保留：那是「这几期不够，看全部」，
            是内容层的延伸链接；这一颗是导航层的常驻入口，两者角色不同。 */}
        <button type="button" className="milhar-head-results tap"
          onClick={() => setRoute(statsRoute)}>
          <History size={15} aria-hidden="true" />
          <span>{t('milharResultsPageTitle')}</span>
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {/* 全局期号条。定稿 §6 —— round.label（#157）是每天午夜归零的日内序号，
            是显示标签不是标识；round.number 才是全局期号，一直在接口里没人用。 */}
        {status.data ? (
          <div className="milhar-idbar">
            {/* **不要在这里重复游戏名** —— 页头已经写了 `Milhar`，写两遍是
                2026-08-19 评审点出来的重复。这一条的职责只有两件：这局怎么开、
                这是第几期。 */}
            <span className="milhar-idbar-cad">{t(tierSpec.cadenceKey)}</span>
            {/* 日内序号（5 分钟档 #001-288，整点档 #01-24），不是五位数的全局
                期号 —— 玩家记不住 #40231，也用不上它。`label` 由后端按开奖时区
                和档位 cadence 算好（MilharTier#roundLabel），与注单历史、档案、
                房间是同一条规则（doc/TIME_DISPLAY_CONTRACT.md 规则 1）。
                全局期号仍在 title 里，客服和链上核对拿得到确切的一期。 */}
            {/* 开奖/定格时钉在**被开的那一期**（stageIdLabel），不跟着 current 跳到
                下一期 —— 见 stageRoundIdLabel。tooltip 里的全局期号也一并跟到那一期，
                客服/链上核对拿到的才是屏幕上正在开的那一期。 */}
            <span className="milhar-idbar-round mono"
              title={(stageOn && stageIdLabel && stageRound
                ? stageRound
                : status.data.round.number)
                ? `global #${stageOn && stageIdLabel && stageRound ? stageRound : status.data.round.number}`
                : undefined}>
              {t('milharRoundIdLabel')} {stageOn && stageIdLabel
                ? stageIdLabel
                : (status.data.round.label || `#${status.data.round.number}`)}
            </span>
          </div>
        ) : null}

        <section className={`milhar-pool${injection ? ' is-injecting' : ''}`}>
          <div className="milhar-pool-label">{t('milharPoolLabel')}</div>
          {pool ? (
            <>
              {/* takeHome，不是 poolTotal —— 见 master §4.1.1 */}
              {/* 金额本身就是入口。「这钱哪来的」这个疑问产生在看数字的那一刻，
                  入口就该在数字上 —— 这也省掉了下面那个单独的 verify 按钮。
                  样式保持不变：金色实底是主 CTA 的语言，这里不能借用（D4）。 */}
              <button type="button" className="milhar-pool-amount-btn tap"
                onClick={() => setRoute(ledgerRoute)}>
                <span className={`milhar-pool-amount mono${injection ? ' is-lift' : ''}`}>
                  {/* alwaysCents：这个数字会随下注实时变动，整数/带分两种写法在 mono
                    下差 42px，不锁死小数位它每跨一次整数边界就横跳一下 */}
                {fmtCurrency(Number(pool.takeHome), pool.currency, undefined, true)}
                </span>
                <ShieldCheck size={13} aria-hidden="true" className="milhar-pool-amount-mark" />
              </button>
              {/* 叙事强化。§6.1：巴西彩票受众数的是期数不是金额 —— 这一行是情绪
                  锚点，比金额本身更能驱动「今天该买了」，所以它得有头条的重量。 */}
              {roundsParts ? (
                <div className="milhar-pool-rounds">
                  {roundsParts.before}
                  <b>{roundsParts.count}</b>
                  {roundsParts.after}
                </div>
              ) : null}
              {/* 本期进度。数据来自买家流的 injected —— 后端早就下发了。 */}
              {hasFeed(status.data?.roundFeed) ? (
                <div className="milhar-pool-injected">
                  {/* roundFeed.injected 是本期**投注总额**（后端 SUM(b.amount)），
                      不是进池额。直接显示它就成了「本期加了 R$26」，而实际只加了
                      25%、屏幕上那个数字只涨 20% —— 虚报 5 倍，而且和每笔飘的
                      +R$0.40 自相矛盾（2026-08-20 评审）。过同一个公式。 */}
                  {t('milharPoolInjected').replace(
                    '%n%',
                    fmtCurrency(
                      injectionFromAmount(status.data.roundFeed.injected, contribPct, retainPct),
                      pool.currency,
                    ),
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <div className="milhar-pool-skeleton sk" />
          )}
          {/* 期号 + 封盘倒计时。视觉规格 §6.2 —— 5 分钟一期的玩法，
              玩家必须知道自己在投哪一期、还剩多久。 */}
          {status.data ? (
            <div className="milhar-roundbar">
              {gate ? (
                /* 三个区块 → Computing，与末尾彩同一套观感。
                   **这里只报状态，一个数字都不出** —— 开奖号在下面的开奖台上逐位落定，
                   两处同时显示只是互相抢注意力（评审 2026-08-19）。
                   闸住期间也没有倒计时数字（视觉规格 §6.2）：那段时间我们在等真实的
                   开奖，任何数字都是编的。 */
                <span className="milhar-roundbar-clock mono is-waiting milhar-gate"
                  role="status"
                  aria-label={`${t('drawing')} #${status.data.drawing?.round ?? ''}`}>
                  <span className="milhar-gate-blocks" aria-hidden="true">
                    {Array.from({ length: GATE_BLOCKS }, (_, i) => (
                      <em key={i} className={
                        i < gate.confirmed ? 'is-on'
                          : i === gate.confirmed ? 'is-next' : ''} />
                    ))}
                  </span>
                  {gate.computing ? t('computingResult') : t('drawing')} {gate.pill}
                </span>
              ) : held ? (
                /* 定格期间**不能掉回倒计时**。下注确实已经放行了，但让一个每秒跳一次的
                   数字和 1.2 秒才落一位的开奖台并排，眼睛必然跟着快的那个走 ——
                   屏幕同时说「下一期还剩 4:20」和「上一期还在开」，揭奖当场变成背景
                   噪音，用户的原话是「跳过去了，没有揭奖的感觉」（2026-08-20 真机）。
                   所以这几秒药丸只报「这是第几期的结果」，**不出数字**（数字在开奖台），
                   等定格结束再把倒计时交还回来。 */
                <span className="milhar-roundbar-clock mono milhar-roundbar-result"
                  role="status">
                  {t('milharResultLabel')} #{held.round}
                </span>
              ) : (
                <span className={`milhar-roundbar-clock mono is-${clock.tone}`}>
                  {clock.clock === null
                    ? `${t('drawing')} \u00b7\u00b7\u00b7`
                    : `${t('closes')} ${clock.clock}`}
                </span>
              )}
            </div>
          ) : null}
          {/* 注入微动。白名单扩展 A —— 定稿 §5 */}
          {injection && pool ? (
            <span className="milhar-pool-plus mono is-go" key={injection.at} aria-hidden="true">
              +{fmtCurrency(injectionFromAmount(injection.amount, contribPct, retainPct), pool.currency, undefined, true)}
            </span>
          ) : null}
          {/* 这里原本还有 verify 按钮和「这钱哪来的」文字链，两者都通向奖池账单，
              而**奖池金额本身就是那个入口**（见上面的 milhar-pool-amount-btn）。
              三个入口指同一个地方，删到只剩长在数字上的那一个。 */}
        </section>

        {/* §7 —— 只在 MADURO 时渲染，位置固定在奖池头与选号器之间 */}
        {pool?.state === 'MADURO' ? (
          <MaduroBanner winningDigits={pool.winningDigits} />
        ) : null}

        <section className="milhar-sec">
          <div className="milhar-sec-head">
            <span className="milhar-sec-title">
              {stageOn && stageRound ? `${t('drawing')} #${stageRound}` : pickPrompt}
            </span>
            {isComplete(pick) && !stageOn ? (
              <button type="button" className="milhar-sec-aux tap"
                onClick={() => { setPick(clearPick(digits)); setPadOpen(0); }}>{t('milharClear')}</button>
            ) : null}
          </div>
          {stageOn ? (
            /* 开奖台。位置、格数、间距与选号器完全一致 —— 眼睛已经认识这四个位置了，
               换布局等于让人重新学一遍。下面的赔率标注不动，落定时跟着亮。 */
            <div className={`milhar-digits is-stage${digits === 5 ? ' is-five' : ''} tier-${stageTier}`}
              role="status"
              aria-label={held ? held.result.split('').join(' ') : t('computingResult')}>
              {cells.map((c, i) => (
                <span key={i}
                  className={`milhar-digit milhar-stage-cell is-${
                    c.state === 'next' && !cellArmed ? 'pending' : c.state}${
                    stageBest && held && i >= digits - stageBest.hits ? ' is-hit' : ''}`}>
                  {c.char ?? '\u2013'}
                </span>
              ))}
              {fireworks.length ? (
                /* 烟花。全部落定之后才放 —— 少一位没落定就先放等于提前剧透结局。
                   规模按赔付分档（见 fireworkCount），不是「中了就一样炸」。 */
                <span className={`milhar-fireworks tier-${stageTier}`} aria-hidden="true">
                  {fireworks.map((p, i) => (
                    <em key={i} style={{
                      '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--d': `${p.delayMs}ms`,
                    } as CSSProperties} />
                  ))}
                </span>
              ) : null}
            </div>
          ) : (
          <div className={`milhar-digits${digits === 5 ? ' is-five' : ''}`}>
            {pick.digits.map((d, i) => (
              <button
                key={i}
                ref={(el) => { cellRefs.current[i] = el; }}
                type="button"
                className={`milhar-digit${i === pick.focus ? ' is-focus' : ''}${d === null ? ' is-empty' : ''}`}
                role="spinbutton"
                aria-label={`${pickPrompt} ${i + 1}/${digits}`}
                aria-valuenow={d ?? undefined}
                aria-expanded={padOpen === i}
                onClick={() => { setPick((prev) => focusAt(prev, i)); setPadOpen(padOpen === i ? null : i); }}
                onKeyDown={(e) => onDigitKey(e, i)}
                onWheel={(e) => setPick((prev) =>
                  setDigit(prev, i, ((prev.digits[i] ?? 0) + (e.deltaY > 0 ? 1 : 9)) % 10))}
              >
                {d === null ? '–' : d}
              </button>
            ))}
          </div>
          )}
          {/* **必须紧跟在格子后面，不能放到十宫格下面。** 十宫格一弹开就会把这一排
              挤到键盘底下，于是 `Jackpot / R$600 / R$40 / R$4` 看起来像在标注
              0-9 那几个键 —— 标注和它描述的格子被拆散了（2026-08-20 真机反馈）。
              这一排的全部意义就是「正上方那一格值多少钱」，位置错了就什么都不是。 */}
          {/* 中奖规则用选号器自己的几何来讲：从右往左对中几位，就拿那一档。
              位置正是选号器已经在展示的东西，所以规则不用被讲述，它被看见 ——
              也和开奖 reveal 的逐位点亮是同一套空间语言。 */}
          <div className={`milhar-payout-row${digits === 5 ? ' is-five' : ''}${
            stageOn ? ' is-stage' : ''}`} aria-hidden="true">
            {payoutSlots.map((slot, i) => {
              /* 缩写，不是全额：这一格固定 54px，而阿根廷的档位动辄七位数,
                 `$ 3.000.000` 直接压到隔壁那一档上（2026-09-15 真机反馈）。
                 缩写由 Intl 按国家语言给：es 是 `3 M`，pt 是 `3 mi`。
                 1.000 以下原样输出，巴西四位档的 600/40/4 一个字都不变。
                 货币符号单独一行小字：这一排每格符号都一样，`AR$` 三个字符
                 占掉五分之一格宽只会再撞一次，而它本来就不是这格要说的信息。 */
              const parts = slot && slot.kind === 'fixed' ? fmtCompactParts(slot.cents / 100, pool?.currency ?? 'BRL') : null;
              return (
                <span key={i} className={`milhar-payout${
                  stageBest && held && stageBest.hits > 0 && i === digits - stageBest.hits
                    ? ' is-hit' : ''}`}>
                  <small className="milhar-payout-sym">{parts?.symbol ?? ''}</small>
                  <span className="milhar-payout-amt">
                    {slot === null ? '' : slot.kind === 'jackpot' ? t('milharPayoutJackpot') : parts?.amount}
                  </span>
                </span>
              );
            })}
          </div>
          <p className="milhar-payout-hint">{t('milharPayoutHint')}</p>
          {/* 对照行：我的号用同一套几何贴在正下方，位一对一。不用文字说「你中了 2 位」，
              两行数字上下一对，眼睛自己会比。没下注就整行不渲染 —— 不放假的对照行，
              也不在开奖这一刻写「你没有参与」，那句话此时说出来是在推销。 */}
          {stageOn && stageRanked.rows.length ? (
            <div className="milhar-stage-mine-list">
              {stageRanked.rows.map((row) => (
                <div key={row.ticket.pick}
                  className={`milhar-digits is-mine${digits === 5 ? ' is-five' : ''}`}
                  aria-label={row.ticket.pick.split('').join(' ')}>
                  {row.ticket.pick.split('').map((d, i) => (
                    <span key={i}
                      className={`milhar-digit milhar-stage-mine${
                        i >= digits - row.hits ? ' is-hit' : ''}`}>
                      {d}
                    </span>
                  ))}
                  {row.sameCount > 1 ? (
                    <span className="milhar-stage-count mono">{`\u00d7${row.sameCount}`}</span>
                  ) : null}
                </div>
              ))}
              {stageRanked.hidden > 0 ? (
                <p className="milhar-stage-more">{`+${stageRanked.hidden}`}</p>
              ) : null}
            </div>
          ) : null}

          {/* 十宫格。视觉规格 §8.2 要求点击和滑动两种输入并存 ——
              手机上没有滚轮，只有滑动的话 Maria 根本选不了号。 */}
          {padOpen !== null && !stageOn ? (
            <div className="milhar-pad" role="listbox"
              aria-label={`${pickPrompt} ${padOpen + 1}/${digits}`}>
              {Array.from({ length: 10 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  role="option"
                  aria-selected={pick.digits[padOpen] === n}
                  className={`milhar-pad-key tap${pick.digits[padOpen] === n ? ' is-on' : ''}`}
                  onClick={() => chooseDigit(padOpen, n)}
                >
                  {n}
                </button>
              ))}
            </div>
          ) : null}
          {/* 反赌徒谬误。从「Como funciona」折叠里搬来的（评审 2026-08-19 删了那段）。
              放这儿比放折叠里对：玩家挑号的这一刻，正是「7777 好久没出了」这种念头
              发生的时刻。**仍然不放统计页** —— 贴在遗漏值旁边会被读成免责声明，
              用户的结论会是「那句是走过场，工具才是真的」（master §9.3.5）。 */}

          {/* 开奖期间收起来：它改的是选号，而选号格此刻被开奖台占着，
              点了什么都不会发生 —— 一个按得动但没反应的按钮比没有按钮糟。 */}
          {stageOn ? null : (
            <button type="button" className="milhar-surpresinha tap"
              onClick={() => { setPick(randomPick(digits, Math.random)); setPadOpen(null); }}>
              <Dices size={16} aria-hidden="true" /> {t('milharSurpresinha')}
            </button>
          )}
          <CoPickerHint pick={pickText} token={token} tier={tier}
            onOpenDistribution={() => setPicksOpen(true)} />
          {error === 'MILHAR_ROUND_RISK_LIMIT' ? (
            <p className="milhar-unavailable">{t('milharUnavailableHint')}</p>
          ) : null}
        </section>

        {/* 更多选项：默认折叠，收起时标题行给当前值摘要 —— 不展开也知道自己选了什么。
            期数从 chips 改成步进器是 2026-08-19 评审定的；步进走的是**档位**
            （1→5→10→20）而不是 +1，否则选 20 期要点 19 下，teimosinha 是营收
            功能，那种摩擦会直接压低它的使用率。 */}
        <button type="button" className="milhar-fold tap" aria-expanded={optionsOpen}
          onClick={() => setOptionsOpen((v) => !v)}>
          <ChevronRight size={14} aria-hidden="true" className="milhar-fold-caret" />
          <span className="milhar-fold-cap">{t('milharMoreOptions')}</span>
          <span className="milhar-fold-val mono">
            {`${rounds} ${t(rounds === 1 ? 'milharUnitRound' : 'milharUnitRounds')} · ${quantity} ${t(quantity === 1 ? 'milharUnitBet' : 'milharUnitBets')}`}
          </span>
        </button>
        {optionsOpen ? (
          <section className="milhar-sec milhar-foldbody">
            <div className="milhar-sec-head">
              <span className="milhar-sec-title">{t('milharRoundsPrompt')}</span>
              {/* §9.1 —— 「现在一次性扣款」必须能被读到：最容易产生客诉的点 */}
              <button type="button" className="milhar-sec-aux tap"
                aria-expanded={teimoTipOpen}
                onClick={() => setTeimoTipOpen((v) => !v)}>
                {t('milharTeimosinha')} ⓘ
              </button>
            </div>
            {teimoTipOpen ? (
              <p className="milhar-teimo-bubble" role="note">{t('milharTeimosinhaHelp')}</p>
            ) : null}
            <div className="milhar-stepper">
              <button type="button" className="milhar-step tap" aria-label="-"
                disabled={rounds <= roundsStep.min}
                onClick={() => setRounds((r) => stepRounds(roundsStep, r, -1))}>−</button>
              <span className="mono milhar-step-value">{rounds}</span>
              <button type="button" className="milhar-step tap" aria-label="+"
                disabled={rounds >= roundsStep.max}
                onClick={() => setRounds((r) => stepRounds(roundsStep, r, 1))}>+</button>
            </div>

            <div className="milhar-sec-head milhar-sec-head-tight">
              <span className="milhar-sec-title">{t('milharQtyPrompt')}</span>
            </div>
            <div className="milhar-stepper">
              <button type="button" className="milhar-step tap" aria-label="-"
                disabled={quantity <= 1}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}>−</button>
              <span className="mono milhar-step-value">{quantity}</span>
              <button type="button" className="milhar-step tap" aria-label="+"
                disabled={quantity >= maxQuantity}
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}>+</button>
            </div>
          </section>
        ) : null}

        <section className="milhar-sec">
          <div className="milhar-summary">
            <span>{summaryLine}</span>
            {priceKnown
              ? <b className="mono">{fmtMoney(total / 100)}</b>
              : <b className="mono milhar-summary-skeleton" aria-busy="true" aria-label={t('loading')} />}
          </div>
          <button
            type="button"
            className={`milhar-cta tap${cta === 'unavailable' ? ' is-unavailable' : ''}`}
            disabled={!priceKnown || cta === 'incomplete' || cta === 'waiting' || cta === 'unavailable' || cta === 'submitting'}
            onClick={submit}
          >
            {ctaLabel}
          </button>
          {/* 参与公开的告知。定稿 §1.1 —— 买家名单会公开身份、金额与号码，
              这件事必须在**买之前**说，而不是买完才发现自己上了墙。
              这是「公开透明」的对称面，不是可选文案。 */}
          {/* 访客在**投入之前**就知道有墙。
              CTA 的 incomplete 排在 guest 前面是有意为之（见 orderMath.test.ts：
              「让他把玩法走完再要求注册」），那是增长打法，本次不推翻。但原来的
              后果是：他认真选完号，按钮才变成「Sign in to bet」——努力之后才撞墙。
              这一行把告知提前，流程不变，意外感消掉。 */}
          <p className="milhar-bet-public">
            {token ? t('milharBetIsPublic') : t('milharGuestFreeToPick')}
          </p>
        </section>

        {/* 社交区移到了 CTA 之后。
            定稿 §3 原本让它紧贴奖池，理由是「它就是奖池为什么在涨的解释」，
            §4.1 更把「换条与奖池 +R$ 同帧」当成整块氛围的立论。**这次推翻那一段。**

            实测（375×812，含访客横幅）：社交区高 182px，压在奖池和选号器之间，
            把选号格顶到屏幕 74% 处、CTA 顶到首屏下方 75px。新用户第一屏看到的是
            「奖金很大 + 别人在玩」，看不到自己能做什么。移走这 182px，选号格上移
            到 419、CTA 落到 705——整个下注动作进首屏。

            因果没有全丢：奖池仍有 `desta rodada · R$ X injetados` 和 +R$ 微动作为
            活信号，只是引起它的那一行不再同屏。这是拿耦合换首屏动作，值。 */}
        <RoundFeed
          feed={status.data?.roundFeed}
          currency={pool?.currency || 'BRL'}
          onOpenBuyers={() => setBuyersOpen(true)}
          onInject={(amount) => setInjection({ amount, at: Date.now() })}
        />

        <section className="milhar-sec" id="milhar-mybets">
          <div className="milhar-sec-head">
            <span className="milhar-sec-title">{t('milharBetsTitle')}</span>
            {/* 「我的票」只覆盖当前期附近（后端 SQL 限定在 currentRound 前后一档），
                几小时后这一注就从这里消失了 —— 所以必须有一个通往完整记录的出口，
                否则玩家买完只能自己想起来去「我的」里翻。 */}
            <button type="button" className="milhar-sec-aux tap"
              onClick={() => { setBetHistoryReturn(milharRoute); setRoute('bet-history'); }}>
              {t('milharMyBetsAll')} ›
            </button>
          </div>
          {currentRoundBets.length === 0 ? (
            <p className="milhar-empty">{t('milharBetsEmpty')}</p>
          ) : (
            currentRoundBets.map((bet) => {
              const cells = ticketCells(bet.pick, bet.matched, bet.matched === digits);
              const outcome = bet.status === 'won'
                ? t('milharWon')
                : bet.status === 'lost'
                  ? t('milharLost')
                  : bet.status === 'cancelled'
                    ? t('milharRefunded')
                    : t('milharPending');
              return (
                <div key={bet.id}
                  className={`milhar-ticket is-${bet.status}${
                    (freshBet?.betId != null && bet.id === freshBet.betId)
                    || (freshBet?.subscriptionId != null
                        && bet.subscriptionId === freshBet.subscriptionId)
                      ? ' is-fresh' : ''}`}>
                  <span className="milhar-ticket-num mono">
                    {bet.pick.split('').map((c, i) => (
                      <span key={i} className={`tone-${cells[i]}`}>{c}</span>
                    ))}
                  </span>
                  <span className="milhar-ticket-meta">
                    {/* 与页顶 ID 条同一套：日内序号。两套编号同屏出现是评审点出的
                        问题，解法是两处都用日内序号，而不是两处都用全局号。 */}
                    <b className="mono" title={bet.round ? `global #${bet.round}` : undefined}>
                      {bet.roundLabel || `#${bet.round}`}
                    </b> · {outcome}<br />
                    {fmtMoney(Number(bet.amount))}
                    {bet.roundsLeft ? ` · ${t('milharMoreRounds').replace('%n%', String(bet.roundsLeft))}` : ''}
                  </span>
                </div>
              );
            })
          )}
        </section>

        {/* 「How it works」。这里原来是「Latest results」那一排 —— 页头右上角已经常驻
            一颗 Results，同屏两个入口通向同一页是重复（奖券页 2026-08 为同一理由删过
            页头那颗）。而新用户在这个位置要的不是「上期开了什么」，是「这几个数怎么来
            的、我怎么算中」。未开奖的期只留在 Your bets 的「还有 N 期」，点开某一期的
            详情走 Results → History（2026-09-15 产品拍板）。 */}
        <section className="milhar-sec">
          <button className="srt-about tap" type="button" onClick={() => setHowOpen(true)}>
            <span className="srt-about-ic is-cyan"><Info size={15} aria-hidden="true" /></span>
            <b>{t('milharHowRow')}</b>
            <ChevronRight size={15} className="srt-about-cv" aria-hidden="true" />
          </button>
        </section>

        {/* §15.1 —— 头奖得主。空数组时整区不渲染：一条都没有时挂个空标题，
            传递的是「没人中过」，比不显示更伤。
            2026-09-11 改：原先是「🎉 某某 levou R$ X com o número Y」两行大白话，
            没有任何时间、点不动、没有身份入口。这个板块的职责是**兑付记录** ——
            大厅天天挂着「N 天没出头奖 · 奖池 R$ X」，而那句话在产品里一直是孤证。
            现在每行两个入口，通向的两个弹层这一页**本来就在用**。 */}
        {(status.data?.jackpotWinners ?? []).length > 0 ? (
          <section className="milhar-sec">
            <div className="milhar-sec-head">
              <span className="milhar-sec-title">{t('milharWinnersTitle')}</span>
            </div>
            <ul className="milhar-winners">
              {(status.data?.jackpotWinners ?? []).map((w, i) => {
                const avatar = winnerAvatarUrl(w);
                const roundText = winnerRoundText(w);
                // 关掉公开榜 / 被封禁或自我排除 / 已注销：后端把 username 连同
                // userId / 头像一起置空（showable=false），派彩这一行仍然下发。
                // 判据用 username 而不是 userId —— 老后端不下发 userId 但仍下发
                // username，那种行是「人名可点」的功能降级，不是匿名。
                const isAnonymous = !w.username;
                const displayName = isAnonymous ? t('milharWinnerAnonymous') : w.username!;
                const canOpenProfile = !!w.userId;
                // 折叠后一行 = 一个 (期号, 用户)；但用户是谁在匿名行上恰恰不下发，
                // 拿不到能唯一区分匿名行的字段。key 不能含 username（同一期可能
                // 同时出现好几条匿名行）——用当次响应里的数组下标，同一期号内足够
                // 区分，整批数据每次轮询都会被整体替换，不存在"下标错位到别的
                // 数据"的问题。
                return (
                  <li key={`${w.round}-${i}`} className="milhar-winner">
                    {/* 两个按钮是**兄弟**，li 不是按钮 —— 按钮嵌按钮是禁令。 */}
                    <button type="button" className="milhar-winner-who tap"
                      disabled={!canOpenProfile}
                      // disabled 时不用「查看资料」这句动作文案——这个按钮点不动，
                      // 读屏器不该听到一个不会发生的动作，只报名字（或匿名占位）。
                      aria-label={canOpenProfile
                        ? t('milharWinnerOpenProfile').replace('%u%', displayName)
                        : displayName}
                      onClick={() => {
                        if (!canOpenProfile || !w.userId) return;
                        setProfileFor({
                          userId: w.userId, username: displayName, avatarUrl: avatar || undefined,
                        });
                      }}>
                      {avatar
                        ? <img className="milhar-feed-av" src={avatar} alt=""
                            width={24} height={24} loading="lazy" />
                        : isAnonymous
                          ? <span className="milhar-feed-av is-anon" aria-hidden="true"
                              style={{ width: 24, height: 24 }}>
                              <UserRound size={12} />
                            </span>
                          : <span className="milhar-feed-av is-letter"
                              style={{ width: 24, height: 24 }}>
                              {displayName.trim().charAt(0).toUpperCase()}</span>}
                      <span className="milhar-winner-name">{displayName}</span>
                    </button>
                    <span className="milhar-winner-amt mono">
                      {fmtMoney(Number(w.payout))}
                    </span>
                    <span className="milhar-winner-meta">
                      {/* 期号用日内序号，全局号进 title —— 与 History、奖池账本
                          同一套身份（doc/TIME_DISPLAY_CONTRACT.md 规则 1）。
                          winnerRoundText 已经处理了「label 自带 #」这件事。
                          匿名行这个按钮照常可点——开奖详情本身是公开证据，身份
                          隐藏不该连带把它也挡住。 */}
                      <button type="button" className="milhar-winner-round tap"
                        title={`global #${w.round}`}
                        aria-label={t('milharWinnerOpenRound').replace('%r%', roundText)}
                        onClick={() => setDetailRound(w.round)}>
                        {roundText}
                      </button>
                      {/* cutoffTs 缺席时**整段省略**，不要渲染 localListTime 的
                          '--' 兜底 —— 那是个看起来像坏了的占位符，而这一行
                          少一个时间仍然完整。 */}
                      {w.cutoffTs ? (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="milhar-winner-time">
                            {localListTime(w.cutoffTs, nowSec)}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

      </div>

      {/* label 与 endsAt 是**当前期**的，所以只有刚下注的那期就是当前期时才能用。
          买多期时首期就是当前期，正常都成立；不成立就退回全局号、不显示时刻，
          而不是把别的一期的时间安到它头上。 */}
      {confirm && status.data ? (
        <BetConfirmSheet
          pick={confirm.pick}
          rounds={confirm.rounds}
          quantity={confirm.quantity}
          totalCents={confirm.totalCents}
          currency={status.data.pool.currency}
          roundNumber={confirm.firstRound}
          roundLabel={confirmIsCurrentRound ? status.data.round.label : undefined}
          drawAt={confirmIsCurrentRound ? status.data.round.endsAt : undefined}
          onSeeBets={() => {
            setConfirm(null);
            document.getElementById('milhar-mybets')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          onAgain={() => { setConfirm(null); setPadOpen(0); }}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {howOpen ? (
        <MilharHowSheet
          tier={tier}
          status={status.data}
          onClose={() => setHowOpen(false)}
          onVerify={(r) => { setHowOpen(false); setVerifyRound(r); }}
        />
      ) : null}
      {verifyRound ? (
        <MilharVerifyOverlay
          round={verifyRound.round}
          tier={tier}
          label={verifyRound.label}
          result={verifyRound.result}
          onClose={() => setVerifyRound(null)}
        />
      ) : null}
      {detailRound != null ? (
        <RoundDetailSheet
          round={detailRound}
          currency={pool?.currency ?? 'BRL'}
          token={token}
          tier={tier}
          onClose={() => setDetailRound(null)}
        />
      ) : null}
      {buyersOpen && hasFeed(status.data?.roundFeed) ? (
        <RoundBuyersSheet
          feed={status.data.roundFeed}
          currency={pool?.currency || 'BRL'}
          roundNumber={status.data.round.number}
          token={token}
          tier={tier}
          onOpenProfile={(userId, username) => setProfileFor({ userId, username })}
          onClose={() => setBuyersOpen(false)}
        />
      ) : null}
      {profileFor ? (
        <PublicProfileSheet
          user={profileSeed(profileFor)}
          onClose={() => setProfileFor(null)}
        />
      ) : null}
      {picksOpen ? (
        <RoundPicksSheet myPick={pickText} token={token} tier={tier}
          onClose={() => setPicksOpen(false)} />
      ) : null}
      {reveal ? (
        <MilharRevealOverlay
          reveal={reveal}
          elapsedMs={revealElapsedMs}
          jackpotDigits={digits}
          onClose={() => setReveal(null)}
        />
      ) : null}
    </section>
  );
}

function MilharRevealOverlay({
  reveal,
  elapsedMs,
  jackpotDigits,
  onClose,
}: {
  reveal: MilharReveal;
  elapsedMs: number;
  jackpotDigits: number;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const digits = reveal.result.split('');
  const complete = digits.every((_, index) =>
    digitStateAt(elapsedMs, index, digits.length) === 'settled');
  // 全屏时刻只能有一个 —— 同期多票时取赢得最多的那张
  const headline = headlineBet(
    reveal.bets.map((bet) => ({ ...bet, payout: Number(bet.payout) })));
  const isJackpot = headline !== null
    && winTier(headline.matched, headline.payout, jackpotDigits) === 'jackpot';
  return (
    <div className="milhar-reveal-stage" role="presentation" onMouseDown={onClose}>
      <section
        className={`milhar-reveal-card${isJackpot ? ' is-jackpot' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('milharRevealTitle').replace('%r%', String(reveal.round))}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="milhar-reveal-close" onClick={onClose} aria-label={t('close')}>
          <X size={18} aria-hidden="true" />
        </button>
        <span className="milhar-reveal-kicker">
          {t('milharRevealTitle').replace('%r%', String(reveal.round))}</span>
        {complete && headline && showsCoinRain(Number(headline.payout)) ? (
          <div className="milhar-coinrain" aria-hidden="true">
            {Array.from({ length: 18 }, (_, i) => (
              <i key={i} style={{ left: `${(i * 5.6) % 100}%`, animationDelay: `${(i % 6) * 0.18}s` }} />
            ))}
          </div>
        ) : null}
        <div className="milhar-reveal-digits mono" aria-live="polite">
          {digits.map((digit, index) => {
            const settled = digitStateAt(elapsedMs, index, digits.length) === 'settled';
            return <span key={index} className={settled ? 'is-settled' : 'is-pending'}>{settled ? digit : '·'}</span>;
          })}
        </div>
        <div className={`milhar-reveal-outcomes${complete ? ' is-visible' : ''}`}>
          {complete ? reveal.bets.map((bet) => {
            const tier = winTier(bet.matched, Number(bet.payout), jackpotDigits);
            return (
              <div key={bet.id}
                className={`milhar-reveal-outcome is-${bet.status} tier-${tier}`}>
                <span className="mono">{bet.pick}</span>
                {/* 赢了就把**金额**说出来 —— 「Você ganhou」不说数字等于没说 */}
                <b>{tier === 'none'
                  ? t('milharLost')
                  : t('milharWinToast').replace('%v%', fmtMoney(Number(bet.payout)))}</b>
              </div>
            );
          }) : <span>{t('drawing')} ···</span>}
        </div>
      </section>
    </div>
  );
}
